/**
 * CIPAA List Page
 *
 * Page for listing and managing CIPAA elections.
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  People as CandidatesIcon,
  Assessment as ResultsIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { PageShell, DataTable, type DataTableColumn, RouteActionButton } from '../../components';
import { pode } from '../../authorization/pode';
import { useAcao, useControle } from '../../authorization/useVeredito';
import { CipaaFilters, CipaaFiltersData, CipaaStatusChip } from './components';
import { Cipaa, CipaaStatus, CipaaFilters as CipaaFiltersType, formatDate } from '../../types/cipaa';
import { cipaaService } from '../../services/cipaaService';

const canEditStatus = (status: CipaaStatus): boolean =>
  [CipaaStatus.DRAFT, CipaaStatus.SCHEDULED].includes(status);

const canDeleteStatus = (status: CipaaStatus): boolean => status === CipaaStatus.DRAFT;

const canManageCandidatesStatus = (status: CipaaStatus): boolean =>
  [
    CipaaStatus.DRAFT,
    CipaaStatus.SCHEDULED,
    CipaaStatus.REGISTRATION_OPEN,
    CipaaStatus.REGISTRATION_CLOSED,
  ].includes(status);

const canViewResultsStatus = (status: CipaaStatus): boolean =>
  [CipaaStatus.VOTING_CLOSED, CipaaStatus.COUNTING, CipaaStatus.COMPLETED].includes(status);

const columns: DataTableColumn<Cipaa>[] = [
  {
    id: 'name',
    label: 'Nome',
    primary: true,
    render: (election) => (
      <>
        <Typography variant="body2" fontWeight="medium">
          {election.name}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Chave: {election.accessKey}
        </Typography>
      </>
    ),
  },
  {
    id: 'company',
    label: 'Empresa',
    render: (election) =>
      election.company?.tradeName || election.company?.legalName || '-',
  },
  {
    id: 'votingPeriod',
    label: 'Período de Votação',
    render: (election) => (
      <>
        <Typography variant="body2">
          {formatDate(election.startDate)} - {formatDate(election.endDate)}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {election.startTime?.substring(0, 5)} - {election.endTime?.substring(0, 5)}
        </Typography>
      </>
    ),
  },
  {
    id: 'members',
    label: 'Vagas',
    render: (election) => (
      <>
        <Typography variant="body2">{election.effectiveMembers} titulares</Typography>
        <Typography variant="caption" color="text.secondary">
          {election.alternateMembers} suplentes
        </Typography>
      </>
    ),
  },
  {
    id: 'status',
    label: 'Status',
    render: (election) => <CipaaStatusChip status={election.status} />,
  },
  {
    id: 'candidates',
    label: 'Candidatos',
    render: (election) => election._counts?.candidates ?? 0,
  },
];

const CipaaListPage: React.FC = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const visualizar = useControle('/cipaa/:id', pode('cipaa.read'), 'Visualizar');
  const verResultados = useAcao('cipaa.read', 'Ver Resultados');
  const excluir = useAcao('cipaa.delete', 'Excluir');
  const editar = useControle('/cipaa/:id/edit', pode('cipaa.update'), 'Editar');
  const gerirCandidatos = useControle('/cipaa/:id/candidates', pode('cipaa.manage-candidates'), 'Gerenciar Candidatos');

  const [elections, setElections] = useState<Cipaa[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [currentFilters, setCurrentFilters] = useState<CipaaFiltersType>({});

  // Delete dialog
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [electionToDelete, setElectionToDelete] = useState<Cipaa | null>(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    loadElections();
  }, [page, rowsPerPage]);

  const loadElections = async (filters?: CipaaFiltersType) => {
    setLoading(true);
    setError(null);
    try {
      const response = await cipaaService.getAll({
        page: page + 1,
        limit: rowsPerPage,
        filters: filters || currentFilters,
      });
      // Handle both formats: { data, pagination } or just array
      const electionsData = Array.isArray(response) ? response : (response.data ?? []);
      const total = Array.isArray(response) ? response.length : (response.pagination?.total || 0);
      setElections(electionsData);
      setTotalCount(total);
    } catch (err: any) {
      setError(err.message ?? 'Erro ao carregar eleições');
      enqueueSnackbar('Erro ao carregar eleições', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleFilter = (filters: CipaaFiltersData) => {
    const apiFilters: CipaaFiltersType = {
      search: filters.search ?? undefined,
      status: filters.status.length > 0 ? (filters.status as CipaaStatus[]) : undefined,
    };
    setCurrentFilters(apiFilters);
    setPage(0);
    loadElections(apiFilters);
  };

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (newRowsPerPage: number) => {
    setRowsPerPage(newRowsPerPage);
    setPage(0);
  };

  const handleView = (election: Cipaa) => {
    navigate(`/cipaa/${election.id}`);
  };

  const handleEdit = (election: Cipaa) => {
    navigate(`/cipaa/${election.id}/edit`);
  };

  const handleManageCandidates = (election: Cipaa) => {
    navigate(`/cipaa/${election.id}/candidates`);
  };

  const handleViewResults = (election: Cipaa) => {
    navigate(`/cipaa/${election.id}/results`);
  };

  const handleDeleteClick = (election: Cipaa) => {
    setElectionToDelete(election);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!electionToDelete) return;

    setDeleting(true);
    try {
      await cipaaService.delete(electionToDelete.id);
      enqueueSnackbar('Eleição excluída com sucesso', { variant: 'success' });
      setDeleteDialogOpen(false);
      setElectionToDelete(null);
      loadElections();
    } catch (err: any) {
      enqueueSnackbar(err.message ?? 'Erro ao excluir eleição', { variant: 'error' });
    } finally {
      setDeleting(false);
    }
  };

  const breadcrumbItems = [
    { label: 'Início' },
    { label: 'Segurança do Trabalho' },
    { label: 'Eleições CIPAA' },
  ];

  return (
    <PageShell
      breadcrumbs={breadcrumbItems}
      title="Eleições CIPAA"
      actions={
        <RouteActionButton
          para="/cipaa/new"
          exige={pode('cipaa.create')}
          variant="contained"
          startIcon={<AddIcon />}
        >
          Nova Eleição
        </RouteActionButton>
      }
    >
        {/* Error Alert */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {/* Filters */}
        <Box sx={{ mb: 3 }}>
          <CipaaFilters onFilter={handleFilter} loading={loading} total={totalCount} />
        </Box>

        {/* Table */}
        <DataTable
          columns={columns}
          rows={elections ?? []}
          getRowId={(election) => election.id}
          loading={loading}
          emptyState={{
            title: 'Nenhuma eleição encontrada',
            description:
              'Cada eleição de CIPA é aberta para uma empresa e concentra o processo: candidatos, votação e apuração.',
          }}
          actions={[
            {
              icon: <ViewIcon fontSize="small" />,
              onClick: handleView,
              ...visualizar,
            },
            {
              icon: <EditIcon fontSize="small" />,
              onClick: handleEdit,
              hidden: (election) => !canEditStatus(election.status),
              ...editar,
            },
            {
              icon: <CandidatesIcon fontSize="small" />,
              color: 'primary',
              onClick: handleManageCandidates,
              hidden: (election) => !canManageCandidatesStatus(election.status),
              ...gerirCandidatos,
            },
            {
              icon: <ResultsIcon fontSize="small" />,
              color: 'success',
              onClick: handleViewResults,
              hidden: (election) => !canViewResultsStatus(election.status),
              ...verResultados,
            },
            {
              icon: <DeleteIcon fontSize="small" />,
              color: 'error',
              onClick: handleDeleteClick,
              hidden: (election) => !canDeleteStatus(election.status),
              ...excluir,
            },
          ]}
          pagination={{
            page,
            rowsPerPage,
            totalCount,
            onPageChange: handlePageChange,
            onRowsPerPageChange: handleRowsPerPageChange,
          }}
        />

        {/* Delete Confirmation Dialog */}
        <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
          <DialogTitle>Confirmar Exclusão</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Tem certeza que deseja excluir a eleição "{electionToDelete?.name}"?
              Esta ação não pode ser desfeita.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteDialogOpen(false)} disabled={deleting}>
              Cancelar
            </Button>
            <Button
              onClick={handleDeleteConfirm}
              color="error"
              variant="contained"
              disabled={deleting}
            >
              {deleting ? 'Excluindo...' : 'Excluir'}
            </Button>
          </DialogActions>
        </Dialog>
    </PageShell>
  );
};

export default CipaaListPage;
